import { useInfiniteQuery } from "@tanstack/react-query";
import { useInHub } from "../lib/hubContext";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  fetchOrderConfirmations,
  type OrderConfirmationReviewStatus,
} from "../api/orderConfirmations";
import {
  FilterToolbar,
  TableEmptyRow,
  TableErrorRow,
  TableHeadCell,
  TableLoadMoreFooter,
  TableLoadingRow,
  TablePanel,
} from "../components/DataTable";
import SearchInput from "../components/SearchInput";
import {
  formatDateShort,
  formatDateTimeShort,
  formatEuro,
  formatQty,
} from "../lib/format";
import { usePersonalView } from "../lib/usePersonalView";
import { useViewSearch } from "../lib/useViewSearch";
import PersonalViewNotice, { PersonalViewReset } from "../components/PersonalViewNotice";

type StatusFilter = "all" | OrderConfirmationReviewStatus;

const PAGE_SIZE = 50;

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "open", label: "Prüfung erforderlich" },
  { value: "query_required", label: "Rückfrage erforderlich" },
  { value: "approved", label: "Freigegeben" },
  { value: "rejected", label: "Nicht freigegeben" },
];

export const ORDER_CONFIRMATION_STATUS_LABELS: Record<
  OrderConfirmationReviewStatus,
  string
> = {
  open: "Prüfung erforderlich",
  query_required: "Rückfrage erforderlich",
  approved: "Freigegeben",
  rejected: "Nicht freigegeben",
};

const STATUS_CLASSES: Record<OrderConfirmationReviewStatus, string> = {
  open: "bg-amber-50 text-amber-800 ring-amber-200",
  query_required: "bg-sky-50 text-sky-800 ring-sky-200",
  approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  rejected: "bg-rose-50 text-rose-800 ring-rose-200",
};

const COMPACT_STATUS_LABELS: Record<OrderConfirmationReviewStatus, string> = {
  open: "Prüfung",
  query_required: "Rückfrage",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
};

export function OrderConfirmationStatusBadge({
  status,
  compactOnMobile = false,
}: {
  status: OrderConfirmationReviewStatus;
  compactOnMobile?: boolean;
}) {
  return (
    <span
      className={
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset " +
        STATUS_CLASSES[status]
      }
    >
      {compactOnMobile ? (
        <>
          <span className="md:hidden">{COMPACT_STATUS_LABELS[status]}</span>
          <span className="hidden md:inline">
            {ORDER_CONFIRMATION_STATUS_LABELS[status]}
          </span>
        </>
      ) : (
        ORDER_CONFIRMATION_STATUS_LABELS[status]
      )}
    </span>
  );
}

export default function OrderConfirmationsPage() {
  const inHub = useInHub();
  const location = useLocation();
  const view = usePersonalView("order_confirmations");
  const { search, setSearch, searchPending } = useViewSearch(view);
  const status = STATUS_OPTIONS.find(option => option.value === view.params.get("status"))?.value ?? "open";
  const debouncedSearch = view.params.get("q")?.trim() ?? "";
  const setStatus = (value: StatusFilter) => {
    if (!view.ready) return;
    const next = new URLSearchParams(view.params);
    if (value === "open") next.delete("status"); else next.set("status", value);
    view.change(next);
  };
  const listTarget = location.pathname + (view.params.size ? `?${view.params}` : "?view=default");
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const worklistQuery = useInfiniteQuery({
    queryKey: [
      "order-confirmations",
      "list",
      { q: debouncedSearch, status },
    ],
    queryFn: ({ pageParam }) =>
      fetchOrderConfirmations({
        q: debouncedSearch || undefined,
        status: status === "all" ? undefined : status,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    // Returning to a worklist must refresh current review decisions.
    staleTime: 0,
    enabled: view.ready && !searchPending,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
  });

  const loading = !view.ready || searchPending || worklistQuery.isLoading || (worklistQuery.isFetching && !worklistQuery.isFetchingNextPage);
  const items = useMemo(
    () => loading ? [] : worklistQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [worklistQuery.data, loading],
  );
  const total = worklistQuery.data?.pages[0]?.total ?? 0;
  const statusCounts = loading || worklistQuery.isError ? null : worklistQuery.data?.pages[0]?.status_counts;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = worklistQuery;
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!hasNextPage) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, loadMore]);

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      {inHub ? null : <header className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900">
          Auftragsbestätigungen prüfen
        </h1>
      </header>}

      <FilterToolbar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Belegnummer oder Großhändler..."
          ariaLabel="Auftragsbestätigung suchen"
          disabled={!view.ready}
          className="w-full basis-full sm:w-auto sm:basis-auto"
        />
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Prüfstatus filtern">
          {STATUS_OPTIONS.map(option => {
            const count = option.value === "all"
              ? statusCounts ? Object.values(statusCounts).reduce((sum, value) => sum + value, 0) : null
              : statusCounts?.[option.value] ?? null;
            return <button key={option.value} type="button" disabled={!view.ready}
              aria-pressed={status === option.value} onClick={() => setStatus(option.value)}
              className={`h-9 rounded-full border px-3.5 text-sm ${status === option.value
                ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
              {option.label}<span aria-hidden="true" className="ml-1.5 tabular-nums text-slate-500">{count === null ? "…" : count.toLocaleString("de-AT")}</span>
            </button>;
          })}
        </div>
        <PersonalViewReset view={view} defaultLabel="Leere Suche und Prüfung erforderlich" className="ml-auto" />
      </FilterToolbar>

      <PersonalViewNotice view={view} defaultLabel="Leere Suche und Prüfung erforderlich" />

      <TablePanel
        title="Einkaufsprüfung"
        subtitle={
          loading
            ? "Lade Auftragsbestätigungen …"
            : total.toLocaleString("de-AT") + " Auftragsbestätigungen"
        }
        footer={
          loading || worklistQuery.isError ? null : (
            <TableLoadMoreFooter
              loadMoreRef={loadMoreRef}
              loadedCount={items.length}
              totalCount={total}
              itemLabel="Auftragsbestätigungen"
              moreItemLabel="Auftragsbestätigungen"
              hasMore={Boolean(hasNextPage)}
              isLoading={isFetchingNextPage}
              buttonLabel="Weitere Auftragsbestätigungen laden"
              loadingLabel="Lade weitere Auftragsbestätigungen …"
              onLoadMore={loadMore}
            />
          )
        }
      >
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="hidden px-3 py-2 text-left font-medium md:table-cell">
                Datum
              </th>
              <TableHeadCell>Belegnummer</TableHeadCell>
              <th className="hidden px-3 py-2 text-left font-medium md:table-cell">
                Großhändler
              </th>
              <th className="hidden px-3 py-2 text-right font-medium md:table-cell">
                Positionen
              </th>
              <th className="hidden px-3 py-2 text-right font-medium md:table-cell">
                Summe
              </th>
              <TableHeadCell>Status</TableHeadCell>
              <th className="hidden px-3 py-2 text-left font-medium md:table-cell">
                Letzte Entscheidung
              </th>
              <th className="hidden px-3 py-2 text-right font-medium md:table-cell">
                <span className="sr-only">Aktion</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <TableLoadingRow
                colSpan={8}
                label="Lade Auftragsbestätigungen …"
              />
            ) : worklistQuery.isError ? (
              <TableErrorRow colSpan={8}>
                Auftragsbestätigungen konnten nicht geladen werden:{" "}
                {(worklistQuery.error as Error).message}
              </TableErrorRow>
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={8}>
                {debouncedSearch
                  ? "Keine Auftragsbestätigung zur Suche gefunden."
                  : "Für diesen Status ist derzeit keine Auftragsbestätigung offen."}
              </TableEmptyRow>
            ) : (
              items.map((item) => {
                const detailHref =
                  "/belege/" + encodeURIComponent(item.order_confirmation_id);
                return (
                  <tr
                    key={item.order_confirmation_id}
                    className="hover:bg-slate-50"
                  >
                    <td className="hidden whitespace-nowrap px-3 py-2 text-slate-700 md:table-cell">
                      {formatDateShort(item.doc_date)}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={detailHref}
                        state={{ from: listTarget }}
                        className="break-all font-mono text-slate-900 hover:text-emerald-700 hover:underline"
                      >
                        {item.doc_number ?? item.order_confirmation_id}
                      </Link>
                      <span className="mt-1 block text-[11px] leading-4 text-slate-500 md:hidden">
                        {formatDateShort(item.doc_date)} · {item.supplier ?? "–"}
                        <span className="block">
                          {formatQty(item.line_count)} Pos. ·{" "}
                          {formatEuro(item.positions_total)}
                        </span>
                        <span className="block">
                          {item.reviewed_at
                            ? formatDateTimeShort(item.reviewed_at) +
                              (item.reviewed_by_label
                                ? " · " + item.reviewed_by_label
                                : "")
                            : "Noch nicht geprüft"}
                        </span>
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-slate-700 md:table-cell">
                      {item.supplier ?? "–"}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-slate-700 md:table-cell">
                      {formatQty(item.line_count)}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-slate-900 md:table-cell">
                      {formatEuro(item.positions_total)}
                    </td>
                    <td className="px-3 py-2">
                      <OrderConfirmationStatusBadge
                        status={item.review_status}
                        compactOnMobile
                      />
                    </td>
                    <td className="hidden px-3 py-2 text-xs text-slate-500 md:table-cell">
                      {item.reviewed_at ? (
                        <>
                          <span>{formatDateTimeShort(item.reviewed_at)}</span>
                          {item.reviewed_by_label ? (
                            <span className="block max-w-[10rem] truncate" title={item.reviewed_by_label}>
                              {item.reviewed_by_label}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        "Noch nicht geprüft"
                      )}
                    </td>
                    <td className="hidden px-3 py-2 text-right md:table-cell">
                      <Link
                        to={detailHref}
                        state={{ from: listTarget }}
                        className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-700 shadow-sm hover:bg-slate-50"
                      >
                        Prüfen
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TablePanel>
    </main>
  );
}
